import {useState, useEffect, useMemo} from 'react'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import {IconButton, TableSortLabel, Popover, Checkbox, Tooltip} from '@mui/material'
import PropTypes from 'prop-types'
import {useTheme} from '@mui/material/styles'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import {
  StyledLogTablePaper,
  StyledLogTableContainer,
  StyledLogTableHeaderCell,
  StyledLogTablePagination,
  StyledPaginationActionsBox,
  StyledViewPermissionLink,
  StyledResetFiltersContainer,
  StyledResetFiltersButton,
  StyledFilterList,
  StyledFilterListItem,
  StyledFilterListItemIcon,
  StyledHeaderCellRow,
  StyledHeaderCellInfoIcon
} from '../../AdminPanelStyling'
import ViewPermissionModal from './components/ViewPermissionModal'
import {PERMISSION_MODAL_TYPES} from './constants/permissionConstants'
import LongTableCell from '../../../components/common/LongTableCell/LongTableCell'

const PaginatedLogTable = ({
  data,
  sortedBy = 'timestamp',
  serverSidePagination = false,
  totalCount = 0,
  currentPage = 0,
  currentPageSize = 5,
  onPageChange = null,
  onPageSizeChange = null,
  searchTerm = '',
  renderLongTableCellFor = []
}) => {
  const [page, setPage] = useState(currentPage)
  const [rowsPerPage, setRowsPerPage] = useState(currentPageSize)
  const [orderBy, setOrderBy] = useState(sortedBy)
  const [order, setOrder] = useState('asc')
  const [sortedData, setSortedData] = useState(data.rows)
  const [permissionModalOpen, setPermissionModalOpen] = useState(false)
  const [selectedPermissionData, setSelectedPermissionData] = useState(null)
  const [permissionModalTitle, setPermissionModalTitle] = useState(PERMISSION_MODAL_TYPES.PROJECT)
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [filterColumn, setFilterColumn] = useState(null)
  const [columnFilters, setColumnFilters] = useState({})

  const handleOpenPermissionModal = (row, title) => {
    setSelectedPermissionData(row)
    setPermissionModalTitle(title)
    setPermissionModalOpen(true)
  }

  const handleClosePermissionModal = () => {
    setPermissionModalOpen(false)
    setSelectedPermissionData(null)
    setPermissionModalTitle(PERMISSION_MODAL_TYPES.PROJECT)
  }

  useEffect(() => {
    setSortedData(data.rows)
    if (!serverSidePagination) {
      setPage(0)
    }
  }, [data.rows, serverSidePagination])

  // Reset column filters only when column definitions change (e.g., switching tabs)
  useEffect(() => {
    setColumnFilters({})
  }, [data.columns])

  // Filter data based on search term and column filters
  const filteredData = useMemo(() => {
    let result = sortedData

    // Apply column filters
    const activeFilters = Object.entries(columnFilters).filter(([, values]) => values.length > 0)
    if (activeFilters.length > 0) {
      result = result.filter((row) =>
        activeFilters.every(([colId, values]) => values.includes(String(row[colId] ?? '')))
      )
    }

    // Apply search term
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase()
      result = result.filter((row) =>
        data.columns.some((column) => {
          const value = row[column.id]
          if (value === null || value === undefined) return false
          return String(value).toLowerCase().includes(lowerSearchTerm)
        })
      )
    }

    return result
  }, [sortedData, searchTerm, data.columns, columnFilters])

  // Reset page when search term changes
  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  // Reset page for column filters only in client-side pagination
  useEffect(() => {
    if (!serverSidePagination) {
      setPage(0)
    }
  }, [columnFilters, serverSidePagination])

  useEffect(() => {
    if (serverSidePagination) {
      setPage(currentPage)
      setRowsPerPage(currentPageSize)
    }
  }, [currentPage, currentPageSize, serverSidePagination])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
    if (serverSidePagination && onPageChange) {
      onPageChange(newPage)
    }
  }

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = +event.target.value
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    if (serverSidePagination && onPageSizeChange) {
      onPageSizeChange(newRowsPerPage)
    }
  }

  const getSortedData = (currentOrderBy) => {
    const tempData = [...data.rows]
    const isSameColumn = currentOrderBy === orderBy
    const toggledOrder = order === 'asc' ? 'desc' : 'asc'
    const newOrder = isSameColumn ? toggledOrder : 'asc'
    const sortMultiplier = newOrder === 'asc' ? 1 : -1

    tempData.sort((a, b) => {
      if (a[currentOrderBy] < b[currentOrderBy]) return -1 * sortMultiplier
      if (a[currentOrderBy] > b[currentOrderBy]) return 1 * sortMultiplier
      return 0
    })

    setOrder(newOrder)
    setOrderBy(currentOrderBy)
    setSortedData(tempData)
  }

  const handleFilterClick = (event, columnId) => {
    event.stopPropagation()
    setFilterColumn(columnId)
    setFilterAnchorEl(event.currentTarget)
  }

  const handleFilterClose = () => {
    setFilterAnchorEl(null)
    setFilterColumn(null)
  }

  const getUniqueColumnValues = (columnId) => {
    // Filter data by search term and all active column filters EXCEPT the current column,
    // so the dropdown only shows values that exist in the currently filtered results
    const otherFilters = Object.entries(columnFilters).filter(
      ([colId, values]) => colId !== columnId && values.length > 0
    )
    let rows = sortedData
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase()
      rows = rows.filter((row) =>
        data.columns.some((column) => {
          const value = row[column.id]
          if (value === null || value === undefined) return false
          return String(value).toLowerCase().includes(lowerSearchTerm)
        })
      )
    }
    if (otherFilters.length > 0) {
      rows = rows.filter((row) => otherFilters.every(([colId, values]) => values.includes(String(row[colId] ?? ''))))
    }
    const values = rows.map((row) => String(row[columnId] ?? ''))
    const uniqueValues = [...new Set(values)]
    const allNumeric = uniqueValues.every((v) => v !== '' && !Number.isNaN(Number(v)))
    if (allNumeric) {
      return uniqueValues.sort((a, b) => Number(a) - Number(b))
    }
    return uniqueValues.sort((a, b) => a.localeCompare(b))
  }

  const handleFilterToggle = (columnId, value) => {
    setColumnFilters((prev) => {
      const current = prev[columnId] || []
      const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
      return {...prev, [columnId]: updated}
    })
  }

  const handleResetAllFilters = () => {
    setColumnFilters({})
  }

  const renderCellValue = (column, row) => {
    const value = row[column.id]

    // Helper to check if row has valid IDs for permission modal (Project and User tabs only)
    const hasValidPermissionIds = row.projectId && row.clientId && row.projectId !== '-' && row.clientId !== '-'

    // Handle values column for Projects tab - show View Permission link only if IDs are valid
    if (column.id === 'values' && row.property === 'Permission' && hasValidPermissionIds) {
      return (
        <StyledViewPermissionLink
          component='button'
          onClick={() => handleOpenPermissionModal(row, PERMISSION_MODAL_TYPES.PROJECT)}
        >
          View Permission
        </StyledViewPermissionLink>
      )
    }

    // Handle permissions column for Users tab - show View Permission link only if IDs are valid
    if (column.id === 'permissions' && value === 'View Permission' && hasValidPermissionIds) {
      return (
        <StyledViewPermissionLink
          component='button'
          onClick={() => handleOpenPermissionModal(row, PERMISSION_MODAL_TYPES.USER)}
        >
          View Permission
        </StyledViewPermissionLink>
      )
    }

    if (column.format && typeof value === 'number') {
      return column.format(value)
    }
    return value
  }

  const hasActiveColumnFilters = Object.values(columnFilters).some((values) => values.length > 0)

  const getDisplayedCount = () => {
    if (serverSidePagination) {
      return totalCount
    }
    return filteredData.length
  }

  // Get rows to display based on pagination mode
  const getDisplayedRows = () => {
    if (serverSidePagination) {
      return filteredData
    }
    return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  }

  const displayedRows = getDisplayedRows()
  const displayedCount = getDisplayedCount()

  return (
    <>
      {hasActiveColumnFilters && (
        <StyledResetFiltersContainer>
          <StyledResetFiltersButton
            variant='outlined'
            size='small'
            startIcon={<RestartAltIcon />}
            onClick={handleResetAllFilters}
          >
            Reset All Filters
          </StyledResetFiltersButton>
        </StyledResetFiltersContainer>
      )}
      <StyledLogTablePaper component={Paper}>
        <StyledLogTableContainer className='log-table-scroll'>
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow>
                {data.columns.map((column) => (
                  <StyledLogTableHeaderCell
                    component={TableCell}
                    key={column.id}
                    align={column.align}
                    style={{minWidth: column.minWidth}}
                    onClick={() => getSortedData(column.id)}
                  >
                    <TableSortLabel active={orderBy === column.id} direction={orderBy === column.id ? order : 'asc'}>
                      <StyledHeaderCellRow>
                        <span>{column.label}</span>
                        {column.truncatedContentInfo && (
                          <Tooltip title={column.truncatedContentInfo}>
                            <StyledHeaderCellInfoIcon />
                          </Tooltip>
                        )}
                      </StyledHeaderCellRow>
                    </TableSortLabel>
                    <IconButton
                      size='small'
                      aria-label={`Filter ${column.label}`}
                      onClick={(e) => handleFilterClick(e, column.id)}
                    >
                      <FilterAltIcon
                        className={`filter-icon${columnFilters[column.id]?.length ? ' active' : ''}${
                          filterColumn === column.id ? ' open' : ''
                        }`}
                      />
                    </IconButton>
                  </StyledLogTableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRows.map((row) => {
                return (
                  <TableRow hover tabIndex={-1} key={row.id}>
                    {data.columns.map((column) => {
                      return renderLongTableCellFor.includes(column.id) ? (
                        <LongTableCell key={column.id + row.id} align={column.align} width={column.minWidth}>
                          <span>{renderCellValue(column, row)}</span>
                        </LongTableCell>
                      ) : (
                        <TableCell key={column.id + row.id} align={column.align}>
                          {renderCellValue(column, row)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </StyledLogTableContainer>
      </StyledLogTablePaper>
      <StyledLogTablePagination
        component={TablePagination}
        rowsPerPageOptions={[5, 10, 25, 50, 75, 100]}
        count={displayedCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
        labelRowsPerPage='Rows per page:'
      />
      <ViewPermissionModal
        open={permissionModalOpen}
        onClose={handleClosePermissionModal}
        permissionData={selectedPermissionData}
        title={permissionModalTitle}
      />
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
      >
        <StyledFilterList>
          {filterColumn &&
            getUniqueColumnValues(filterColumn).map((value) => (
              <StyledFilterListItem key={value} tabIndex={0} onClick={() => handleFilterToggle(filterColumn, value)}>
                <StyledFilterListItemIcon>
                  <Checkbox
                    edge='start'
                    size='small'
                    checked={columnFilters[filterColumn]?.includes(value) || false}
                    disableRipple
                    readOnly
                  />
                </StyledFilterListItemIcon>
                {value || '-'}
              </StyledFilterListItem>
            ))}
        </StyledFilterList>
      </Popover>
    </>
  )
}

export default PaginatedLogTable

function TablePaginationActions(props) {
  const theme = useTheme()
  const {count, page, rowsPerPage, onPageChange} = props

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0)
  }

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1)
  }

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
  }

  return (
    <StyledPaginationActionsBox>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label='first page'>
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label='previous page'>
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>

      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='next page'
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='last page'
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </StyledPaginationActionsBox>
  )
}

PaginatedLogTable.propTypes = {
  data: PropTypes.shape({
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired
  }).isRequired,
  sortedBy: PropTypes.string,
  serverSidePagination: PropTypes.bool,
  totalCount: PropTypes.number,
  currentPage: PropTypes.number,
  currentPageSize: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  searchTerm: PropTypes.string,
  renderLongTableCellFor: PropTypes.array
}

PaginatedLogTable.defaultProps = {
  sortedBy: 'timestamp',
  serverSidePagination: false,
  totalCount: 0,
  currentPage: 0,
  currentPageSize: 5,
  onPageChange: null,
  onPageSizeChange: null,
  searchTerm: '',
  renderLongTableCellFor: []
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired
}
